/**
 * Dev-only: forces onboarding to restart on every fresh sign-in of the dedicated
 * test account (onboardingtest@test.com), so the new-user onboarding flow can be
 * tested repeatedly without manually clicking the dev reset button each time.
 *
 * Mechanism: compare the live Clerk session id against the last one we already
 * reset for (persisted in localStorage). A NEW session id means a new sign-in ->
 * wipe the account and reload into onboarding. The SAME session id (a page reload,
 * or finishing onboarding within that session) does nothing, so the tester can
 * actually walk through the flow instead of being wiped mid-run.
 *
 * Reset-on-sign-IN (not sign-out) is deliberate: sign-out is client-side, so a
 * tab close or crash would skip it and leave stale state on the next login. A
 * session-id change fires no matter how the previous session ended.
 *
 * Hard-gated to dev (`isDev`) and to this one email; the server endpoint it
 * drives (POST /api/account/reset) is independently dev-gated and 403s in prod.
 */
export const ONBOARDING_TEST_EMAIL = 'onboardingtest@test.com';

/** localStorage key holding the Clerk session id we last reset for. */
export const ONBOARDING_TEST_GUARD_KEY = 'oqa_onboarding_test_last_session';

export interface OnboardingTestResetInput {
  /** import.meta.env.DEV from the caller — false in the Vercel prod build. */
  isDev: boolean;
  /** The signed-in user's primary email, if any. */
  email: string | null | undefined;
  /** The live Clerk session id. */
  sessionId: string | null | undefined;
  /** The session id we last reset for (from localStorage), if any. */
  lastHandledSessionId: string | null | undefined;
}

/**
 * Pure decision: should we wipe + restart onboarding on this render?
 *
 * True only in dev, for the test email, with a real session id we have NOT
 * already reset for. Everything else is false.
 */
export function shouldResetOnboardingTest(input: OnboardingTestResetInput): boolean {
  if (!input.isDev) return false;
  if (!input.email) return false;
  if (input.email.trim().toLowerCase() !== ONBOARDING_TEST_EMAIL) return false;
  if (!input.sessionId) return false;
  if (input.lastHandledSessionId === input.sessionId) return false;
  return true;
}
